import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, dateFr, FACTURE_STATUS } from "@/lib/constants";
import { generatePdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, FileDown, Pencil, Copy, Trash2, CheckCircle2, Send, Lock, History, FileMinus, Ban } from "lucide-react";

export default function FactureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [settings, setSettings] = useState({});
  const [confirm, setConfirm] = useState(null); // "emit" | "delete" | "avoir" | "cancel"
  const [showHistory, setShowHistory] = useState(false);

  const load = () => api.get(`/factures/${id}`).then((r) => setF(r.data)).catch(() => { toast.error("Facture introuvable"); navigate("/factures"); });

  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)); /* eslint-disable-next-line */ }, [id]);

  const act = async (fn, okMsg) => {
    try { const { data } = await fn(); setF(data); if (okMsg) toast.success(okMsg); return data; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); return null; }
  };

  if (!f) return <div className="p-8 text-slate-500">Chargement…</div>;

  const isAvoir = f.doc_type === "avoir";
  const isDraft = f.status === "brouillon" && !f.locked;
  const isCancelled = f.status === "annulee_par_avoir";
  const reste = Number(f.total_ttc) - Number(f.amount_paid || 0);

  const emit = () => { setConfirm(null); act(() => api.post(`/factures/${id}/emit`), "Facture émise et verrouillée"); };
  const del = async () => { setConfirm(null); try { await api.delete(`/factures/${id}`); toast.success("Brouillon supprimé"); navigate("/factures"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const duplicate = async () => { const d = await act(() => api.post(`/factures/${id}/duplicate`), "Brouillon dupliqué"); if (d) navigate(`/factures/${d.id}`); };
  const send = () => act(() => api.post(`/factures/${id}/send`), "Facture marquée envoyée");
  const doAvoir = (cancel) => { setConfirm(null); act(() => api.post(`/factures/${id}/avoir?cancel=${cancel}`), cancel ? "Avoir créé, facture annulée" : "Avoir créé").then((d) => { load(); }); };
  const setPayment = (patch) => act(() => api.post(`/factures/${id}/payment`, patch), "Paiement mis à jour");

  const title = isAvoir ? f.number : (f.number || "Facture (brouillon)");

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-4xl mx-auto">
      <button data-testid="back-button" onClick={() => navigate("/factures")} className="flex items-center gap-2 text-slate-600 font-semibold hover:text-slate-900">
        <ArrowLeft size={20} /> Retour aux factures
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
            <span data-testid="facture-status-badge" className={`text-xs font-semibold px-2.5 py-1 rounded ${FACTURE_STATUS[f.status]?.cls}`}>{FACTURE_STATUS[f.status]?.label || f.status}</span>
            {f.locked && !isAvoir && <span className="flex items-center gap-1 text-xs text-slate-500"><Lock size={13} /> Verrouillée</span>}
          </div>
          <p className="text-slate-600 mt-1">
            {isAvoir ? `Avoir sur facture ${f.facture_origine_number}` : `Issue du devis ${f.devis_number || "—"}`} · {f.client?.name}
          </p>
        </div>
        <Button data-testid="facture-pdf-button" onClick={() => generatePdf(f, settings, isAvoir ? "avoir" : "facture")} className="h-12 bg-[#0284C7] hover:bg-[#0369A1] font-bold"><FileDown size={18} className="mr-1" />{isDraft ? "Aperçu PDF" : "Télécharger PDF"}</Button>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2">
        {isDraft && (
          <>
            <Button data-testid="facture-edit-button" variant="outline" onClick={() => navigate(`/factures/${id}/edit`)} className="h-11 border-[#0284C7] text-[#0284C7] font-bold"><Pencil size={16} className="mr-1" />Modifier</Button>
            <Button data-testid="facture-emit-button" onClick={() => setConfirm("emit")} className="h-11 bg-[#16A34A] hover:bg-[#15803D] font-bold"><CheckCircle2 size={16} className="mr-1" />Valider et émettre</Button>
            <Button data-testid="facture-duplicate-button" variant="outline" onClick={duplicate} className="h-11 border-slate-300"><Copy size={16} className="mr-1" />Dupliquer</Button>
            <Button data-testid="facture-delete-button" variant="outline" onClick={() => setConfirm("delete")} className="h-11 border-red-300 text-red-600"><Trash2 size={16} className="mr-1" />Supprimer</Button>
          </>
        )}
        {!isDraft && !isAvoir && (
          <>
            {f.status === "emise" && <Button data-testid="facture-send-button" variant="outline" onClick={send} className="h-11 border-indigo-300 text-indigo-700 font-semibold"><Send size={16} className="mr-1" />Marquer envoyée</Button>}
            <Button data-testid="facture-duplicate-button" variant="outline" onClick={duplicate} className="h-11 border-slate-300"><Copy size={16} className="mr-1" />Dupliquer</Button>
            {!isCancelled && <Button data-testid="facture-avoir-button" variant="outline" onClick={() => setConfirm("avoir")} className="h-11 border-amber-300 text-amber-700"><FileMinus size={16} className="mr-1" />Créer un avoir</Button>}
            {!isCancelled && <Button data-testid="facture-cancel-button" variant="outline" onClick={() => setConfirm("cancel")} className="h-11 border-red-300 text-red-600"><Ban size={16} className="mr-1" />Annuler par avoir</Button>}
            {f.avoir_number && <Button variant="ghost" onClick={() => { /* find avoir */ api.get("/factures").then((r) => { const a = r.data.find((x) => x.number === f.avoir_number); if (a) navigate(`/factures/${a.id}`); }); }} className="h-11 text-slate-600">Voir l'avoir {f.avoir_number}</Button>}
          </>
        )}
        <Button data-testid="facture-history-button" variant="ghost" onClick={() => setShowHistory(true)} className="h-11 text-slate-600"><History size={16} className="mr-1" />Historique</Button>
      </div>

      {/* Payment management (only for emitted, non-avoir, non-cancelled) */}
      {!isDraft && !isAvoir && !isCancelled && (
        <div className="bg-white border border-slate-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Montant payé (€)</Label>
            <Input type="number" step="0.01" data-testid="facture-amount-paid" className="h-12" defaultValue={f.amount_paid || 0} onBlur={(e) => setPayment({ amount_paid: Number(e.target.value) })} />
          </div>
          <div className="space-y-2">
            <Label>Date d'échéance</Label>
            <Input type="date" data-testid="facture-echeance" className="h-12" defaultValue={f.date_echeance} onBlur={(e) => setPayment({ date_echeance: e.target.value })} />
          </div>
          <div className="space-y-2 flex flex-col justify-end">
            <div className="flex gap-2">
              <Button data-testid="mark-partial" variant="outline" onClick={() => setPayment({ amount_paid: Math.max(0.01, Number(f.total_ttc) / 2) })} className="flex-1 border-amber-300 text-amber-700">Partielle</Button>
              <Button data-testid="mark-paid" onClick={() => setPayment({ amount_paid: Number(f.total_ttc) })} className="flex-1 bg-[#16A34A] hover:bg-[#15803D]">Payée</Button>
            </div>
          </div>
          <div className="md:col-span-3 flex flex-wrap items-center gap-4">
            <span className="text-slate-600">Émise le {dateFr(f.date_emission)}</span>
            <span className="text-slate-600">Reste à payer : <span className="font-bold text-slate-900">{eur(reste)}</span></span>
          </div>
        </div>
      )}

      {isCancelled && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700 text-sm font-medium">Cette facture a été annulée par l'avoir {f.avoir_number}. Elle reste conservée pour l'historique.</div>
      )}

      {/* Equipment */}
      {f.equipment && (f.equipment.material_type || f.equipment.brand_model || f.equipment.power || (f.equipment.accessoires || []).length > 0) && (
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-heading text-lg font-bold mb-2 text-[#16A34A]">Matériel installé</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-slate-700">
            {f.equipment.material_type && <div><span className="text-slate-500">Type : </span>{f.equipment.material_type}</div>}
            {f.equipment.brand_model && <div><span className="text-slate-500">Marque / Modèle : </span>{f.equipment.brand_model}</div>}
            {f.equipment.power && <div><span className="text-slate-500">Puissance : </span>{f.equipment.power}</div>}
            {(f.equipment.accessoires || []).length > 0 && <div className="sm:col-span-2"><span className="text-slate-500">Accessoires : </span>{f.equipment.accessoires.join(", ")}</div>}
          </div>
        </div>
      )}

      {/* Lines */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left p-3">Désignation</th>
              <th className="text-left p-3 hidden md:table-cell">Type</th>
              <th className="text-center p-3">Qté</th>
              <th className="text-right p-3">P.U. HT</th>
              <th className="text-center p-3">TVA</th>
              <th className="text-right p-3">Total HT</th>
            </tr>
          </thead>
          <tbody>
            {f.lines.map((l, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-3 font-medium text-slate-900">{l.designation}</td>
                <td className="p-3 hidden md:table-cell text-slate-600">{l.prestation_type}</td>
                <td className="p-3 text-center">{l.quantity}</td>
                <td className="p-3 text-right">{eur(l.unit_price_ht)}</td>
                <td className="p-3 text-center">{Number(l.tva_rate) === 0 ? "Non appl." : `${l.tva_rate}%`}</td>
                <td className="p-3 text-right font-semibold">{eur(Number(l.quantity) * Number(l.unit_price_ht))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-xs bg-white border border-slate-200 rounded-md p-6 space-y-3">
          {Math.abs(Number(f.discount_amount)) > 0 && (
            <>
              <div className="flex justify-between text-slate-600"><span>Sous-total HT</span><span className="font-semibold text-slate-900">{eur(f.subtotal_ht)}</span></div>
              <div className="flex justify-between text-[#16A34A]"><span>Remise{f.discount_type === "percent" ? ` (${Number(f.discount_value)}%)` : ""}</span><span className="font-semibold">- {eur(Math.abs(f.discount_amount))}</span></div>
            </>
          )}
          <div className="flex justify-between text-slate-600"><span>Total HT</span><span className="font-semibold text-slate-900">{eur(f.total_ht)}</span></div>
          <div className="flex justify-between text-slate-600"><span>{Number(f.total_tva) === 0 ? "TVA non applicable" : "TVA"}</span><span className="font-semibold text-slate-900">{eur(f.total_tva)}</span></div>
          <div className="border-t border-[#0284C7] pt-3 flex justify-between items-center">
            <span className="font-heading font-bold text-lg">{isAvoir ? "Total avoir" : "Total TTC"}</span>
            <span className="font-heading font-extrabold text-2xl text-[#0284C7]">{eur(f.total_ttc)}</span>
          </div>
        </div>
      </div>

      {/* Emit confirm */}
      <AlertDialog open={confirm === "emit"} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Valider et émettre la facture ?</AlertDialogTitle>
            <AlertDialogDescription>
              Après émission : la facture recevra son <b>numéro officiel</b> (chronologique et continu), elle sera <b>verrouillée</b> et ne pourra plus être supprimée ni modifiée directement. Toute correction devra passer par un avoir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-emit" onClick={emit} className="bg-[#16A34A] hover:bg-[#15803D]">Valider et émettre</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={confirm === "delete"} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce brouillon ?</AlertDialogTitle>
            <AlertDialogDescription>Ce brouillon de facture sera définitivement supprimé. Le devis d'origine n'est pas affecté.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-facture" onClick={del} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Avoir confirm */}
      <AlertDialog open={confirm === "avoir" || confirm === "cancel"} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "cancel" ? "Annuler la facture par un avoir ?" : "Créer un avoir ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              Un avoir (note de crédit) sera généré avec un numéro officiel pour un montant de {eur(f.total_ttc)}.
              {confirm === "cancel" ? " La facture sera marquée « Annulée par avoir » mais conservée pour l'historique." : " La facture restera en l'état."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-avoir" onClick={() => doAvoir(confirm === "cancel")} className="bg-amber-600 hover:bg-amber-700">Créer l'avoir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl">Historique</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(f.history || []).length === 0 && <p className="text-slate-500 text-sm">Aucun événement enregistré.</p>}
            {(f.history || []).slice().reverse().map((h, i) => (
              <div key={i} data-testid={`history-row-${i}`} className="border-b border-slate-100 py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-slate-900">{h.action}</span>
                  <span className="text-slate-400 shrink-0">{dateFr(h.date)}</span>
                </div>
                <div className="text-xs text-slate-500">{h.user}{(h.old_value !== null && h.old_value !== undefined) ? ` · ${h.old_value} → ${h.new_value}` : ""}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
